
import { useState, useMemo, useEffect, useRef, useCallback, useReducer } from "react";
import { FilePlus, Trash2, Search, FileText, StickyNote, Loader2 } from "lucide-react";
import { DeleteConfirmation } from "./DeleteConfirmation";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";

import { supabase } from "@/lib/supabase";
import { usePermissions } from '@/contexts/PermissionsContext';
import { RealtimeChannel } from '@supabase/supabase-js';


interface Note {
  id: string;
  title: string;
  content: string;
  room_id: string;
  created_by: string;
  position_x: number;
  position_y: number;
  width: number;
  height: number;
  color: string;
  created_at: string;
  updated_at: string;
  // Add these for backward compatibility with existing code
  createdAt?: string;
  updatedAt?: string;
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric', 
    month: 'short', 
    day: 'numeric', 
    hour: '2-digit', 
    minute: '2-digit',
    hour12: true
  }).format(date);
};

interface NotesViewProps {
  roomId: string;
}

export const NotesView = ({ roomId }: NotesViewProps) => {
  const { permissions } = usePermissions();
  const isReadOnly = !(permissions?.can_write_notes);
  
  // State management
  const [localNotes, setLocalNotes] = useState<Note[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState<Note | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [subscription, setSubscription] = useState<RealtimeChannel | null>(null);
  const [activeNoteId, setActiveNoteId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const titleInputRef = useRef<HTMLInputElement>(null);
  const debounceTimersRef = useRef<{[key: string]: NodeJS.Timeout}>({});

  // Helper functions
  const clearSearch = useCallback(() => setSearchTerm(''), []);

  // Load notes initially and set up real-time subscription
  useEffect(() => {
    if (!roomId) return;

    const fetchNotes = async () => {
      if (!roomId) return;
      
      try {
        setIsLoading(true);
        const { data, error } = await supabase
          .from('notes')
          .select('*')
          .eq('room_id', roomId)
          .order('created_at', { ascending: false });

        if (error) throw error;
        
        const formattedNotes = (data || []).map((note: any) => ({
          ...note,
          createdAt: note.created_at,
          updatedAt: note.updated_at
        })) as Note[];
        setLocalNotes(formattedNotes);
        
      } catch (err) {
        console.error('Error loading notes:', err);
        setError('Failed to load notes');
      } finally {
        setIsLoading(false);
      }
    };

    fetchNotes();

    // Set up real-time subscription for notes
    console.log('Setting up real-time subscription for room:', roomId);
    
    const channelName = `room_${roomId}_notes`;
    let retryCount = 0;
    const maxRetries = 5;
    let retryTimeout: NodeJS.Timeout;
    
    // Clean up any existing subscription
    const cleanupSubscription = () => {
      if (subscription) {
        console.log('Cleaning up existing subscription');
        try {
          subscription.unsubscribe();
          supabase.removeChannel(subscription);
        } catch (e) {
          console.error('Error cleaning up subscription:', e);
        } finally {
          setSubscription(null);
        }
      }
    };
    
    // Function to handle subscription retries with exponential backoff
    const setupSubscription = () => {
      cleanupSubscription();
      
      console.log('Creating new subscription channel:', channelName);
      
      // Create a new channel with proper configuration
      const notesSubscription = supabase.channel(channelName, {
        config: {
          broadcast: { self: true, ack: true },
          presence: { key: `room_${roomId}` },
        },
      });
      
      console.log('Channel created with config:', notesSubscription.topic);
      
      // Set up real-time subscription for notes table
      notesSubscription
        .on('postgres_changes', 
          {
            event: '*',
            schema: 'public',
            table: 'notes',
            filter: `room_id=eq.${roomId}`
          },
          (payload: any) => {
            console.log('Postgres Changes Event (direct):', payload);
            // The main handler will process this event
          }
        );
      
      console.log('Channel created, setting up listeners...');
      
      // Handle subscription state changes
      notesSubscription
        .on('broadcast', { event: 'note_deleted' }, (payload: any) => {
          console.log('=== BROADCAST RECEIVED ===');
          console.log('Broadcast payload:', payload);
          
          const { noteId, roomId: broadcastRoomId } = payload.payload;
          if (broadcastRoomId === roomId) {
            console.log(`Processing broadcast delete for note ${noteId}`);
            
            // Optimistically remove the note from local state
            setLocalNotes(prevNotes => {
              const updatedNotes = prevNotes.filter(note => note.id !== noteId);
              console.log('Notes after broadcast delete:', updatedNotes.length);
              return updatedNotes;
            });
            
            // Clear active note if it was deleted
            if (activeNoteId === noteId) {
              console.log('Clearing active note as it was deleted via broadcast');
              setActiveNoteId(null);
            }
          }
        })
        .on('presence', { event: 'sync' }, () => {
          console.log('Presence state synchronized');
        })
        .on('system' as any, 'CHANNEL_UPDATE', (event: any) => {
          console.log('System event:', event);
          
          if (event.status === 'CHANNEL_ERROR') {
            console.error('Channel error:', event);
            handleReconnect();
          } else if (event.status === 'CHANNEL_CLOSED') {
            console.log('Channel closed, will attempt to reconnect...');
            handleReconnect();
          } else if (event.status === 'CHANNEL_OPENED') {
            console.log('Channel opened successfully');
            retryCount = 0; // Reset retry count on successful connection
          }
        })
        .on('postgres_changes', 
          {
            event: '*',
            schema: 'public',
            table: 'notes',
            filter: `room_id=eq.${roomId}`
          },
          (payload: any) => {
            console.log('=== REALTIME EVENT ===');
            console.log('Channel:', channelName);
            console.log('Event type:', payload.eventType);
            console.log('Full payload:', JSON.stringify(payload, null, 2));
            
            // Handle the update based on the event type
            switch (payload.eventType) {
              case 'INSERT': {
                const newNote = payload.new as Note;
                console.log('Processing INSERT for note:', newNote.id);
                setLocalNotes(prevNotes => {
                  // Check if note already exists to avoid duplicates
                  if (!prevNotes.some(n => n.id === newNote.id)) {
                    return [{
                      ...newNote,
                      createdAt: newNote.created_at,
                      updatedAt: newNote.updated_at
                    }, ...prevNotes];
                  }
                  return prevNotes;
                });
                break;
              }
              case 'UPDATE': {
                const updatedNote = payload.new as Note;
                console.log('Processing UPDATE for note:', updatedNote.id);
                setLocalNotes(prevNotes => 
                  prevNotes.map(note => 
                    note.id === updatedNote.id 
                      ? { 
                          ...updatedNote, 
                          createdAt: updatedNote.created_at || note.createdAt,
                          updatedAt: updatedNote.updated_at || note.updatedAt
                        }
                      : note
                  )
                );
                break;
              }
              case 'DELETE': {
                // For DELETE events, the ID is in the old object
                // Try multiple ways to get the ID as a fallback
                const deletedNoteId = payload.old?.id || 
                                    (payload.old && payload.old.id) || 
                                    (payload.record_id) ||
                                    (payload.record && payload.record.id);
                                    
                console.log('Processing DELETE event with payload:', payload);
                console.log('Extracted note ID for deletion:', deletedNoteId);
                
                if (!deletedNoteId) {
                  console.error('Could not determine note ID from DELETE payload:', payload);
                  break;
                }
                
                // Remove the note from local state
                setLocalNotes(prevNotes => {
                  const updatedNotes = prevNotes.filter(note => note.id !== deletedNoteId);
                  console.log('Notes after deletion:', updatedNotes);
                  return updatedNotes;
                });
                
                // Clear active note if it was deleted
                if (activeNoteId === deletedNoteId) {
                  console.log('Clearing active note as it was deleted');
                  setActiveNoteId(null);
                }
                break;
              }
              default: {
                console.log('Unhandled event type:', payload.eventType);
              }
            }
          }
        )
        .subscribe((status, err) => {
          console.log('Subscription status:', status);
          if (err) {
            console.error('Subscription error:', err);
            handleReconnect();
          } else if (status === 'SUBSCRIBED') {
            console.log('Successfully subscribed to channel:', channelName);
            retryCount = 0; // Reset retry count on successful subscription
          }
        });
      
      setSubscription(notesSubscription);
      return notesSubscription;
    };
    
    // Handle reconnection with exponential backoff
    const handleReconnect = () => {
      if (retryCount >= maxRetries) {
        console.error('Max retry attempts reached, giving up');
        return;
      }
      
      const delay = Math.min(1000 * Math.pow(2, retryCount), 30000); // Max 30s delay
      retryCount++;
      
      console.log(`Attempting to reconnect (${retryCount}/${maxRetries}) in ${delay}ms`);
      
      retryTimeout = setTimeout(() => {
        setupSubscription();
      }, delay);
    };
    
    // Initial subscription setup
    const currentSubscription = setupSubscription();
    
    // Cleanup function
    return () => {
      console.log('Cleaning up subscription');
      clearTimeout(retryTimeout);
      if (currentSubscription) {
        currentSubscription.unsubscribe();
        supabase.removeChannel(currentSubscription);
      }
    };
  }, [roomId]);

  // Load notes when roomId changes
  const loadNotes = useCallback(async () => {
    if (!roomId) return;
    
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('notes')
        .select('*')
        .eq('room_id', roomId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      const formattedNotes = (data || []).map((note: any) => ({
        ...note,
        createdAt: note.created_at,
        updatedAt: note.updated_at
      })) as Note[];
      setLocalNotes(formattedNotes);
      
    } catch (error) {
      console.error('Error loading notes:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to load notes';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [roomId]);

  // Load notes when roomId changes
  useEffect(() => {
    if (roomId) {
      loadNotes();
    }
  }, [roomId, loadNotes]);

  // Debounced update function
  const debouncedUpdateNote = useCallback((id: string, updates: Partial<Note>) => {
    // Clear any pending updates for this note
    if (debounceTimersRef.current?.[id]) {
      clearTimeout(debounceTimersRef.current[id]);
    }

    // Set a new timer
    const timerId = setTimeout(async () => {
      try {
        const { error } = await supabase
          .from('notes')
          .update({
            ...updates,
            updated_at: new Date().toISOString()
          })
          .eq('id', id);

        if (error) throw error;
      } catch (err) {
        console.error('Error updating note:', err);
        // Revert the local state on error
        loadNotes();
      } finally {
        // Clean up the timer
        if (debounceTimersRef.current?.[id] === timerId) {
          const newTimers = { ...debounceTimersRef.current };
          delete newTimers[id];
          debounceTimersRef.current = newTimers;
        }
      }
    }, 1000); // 1 second debounce

    // Store the timer ID
    debounceTimersRef.current = {
      ...debounceTimersRef.current,
      [id]: timerId
    };
  }, [loadNotes]);

  // Clean up timers and subscription on unmount
  useEffect(() => {
    return () => {
      // Clear all debounce timers
      if (debounceTimersRef.current) {
        Object.values(debounceTimersRef.current).forEach(timer => {
            if (timer) clearTimeout(timer);
        });
      }
      
      // Unsubscribe from real-time updates
      if (subscription) {
        supabase.removeChannel(subscription);
      }
    };
  }, [subscription]);
  
  const sortedNotes = useMemo(() => {
    return [...localNotes]
      .filter(note => 
        (note.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
         note.content?.toLowerCase().includes(searchTerm.toLowerCase()))
      )
      .sort((a, b) => new Date(b.updated_at || b.created_at).getTime() - 
                     new Date(a.updated_at || a.created_at).getTime());
  }, [localNotes, searchTerm]);

  const activeNote = useMemo(() => {
    return localNotes.find(note => note.id === activeNoteId) || null;
  }, [localNotes, activeNoteId]);

  // Auto-focus and select title for new, untitled notes
  useEffect(() => {
    if (activeNote && activeNote.title === 'Untitled Note' && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [activeNote, titleInputRef]);

  const addNewNote = async () => {
    if (!roomId) return;
    
    try {
      setIsLoading(true);
      
      // Create a temporary ID for optimistic update
      const tempId = `temp-${Date.now()}`;
      
      const newNote: Note = {
        id: tempId,
        title: 'Untitled Note',
        content: '',
        room_id: roomId,
        created_by: 'Anonymous',
        position_x: 0,
        position_y: 0,
        width: 300,
        height: 200,
        color: '#ffffff',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      } as Note;

      // Optimistically add the note to local state
      setLocalNotes(prevNotes => [newNote, ...prevNotes]);
      setActiveNoteId(tempId);
      clearSearch();

      // Save to database - only include fields that exist in the database
      const { data, error } = await supabase
        .from('notes')
        .insert([{
          title: newNote.title,
          content: newNote.content,
          room_id: newNote.room_id,
          created_by: newNote.created_by,
          position_x: newNote.position_x,
          position_y: newNote.position_y,
          width: newNote.width,
          height: newNote.height,
          color: newNote.color,
          created_at: newNote.created_at,
          updated_at: newNote.updated_at
        }])
        .select()
        .single();

      if (error) throw error;
      
      // Update local state with the server-generated ID
      if (data?.id) {
        setLocalNotes(prevNotes => 
          prevNotes.map(note => {
            if (note.id === tempId) {
              const updatedNote = {
                ...note,
                id: String(data.id),
                created_at: typeof data.created_at === 'string' ? data.created_at : new Date().toISOString(),
                updated_at: typeof data.updated_at === 'string' ? data.updated_at : new Date().toISOString()
              };
              return updatedNote;
            }
            return note;
          })
        );
        setActiveNoteId(String(data.id));
      }
    } catch (err: unknown) {
      console.error('Error creating note:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to create note';
      setError(errorMessage);
      
      // Remove the optimistic update if there was an error
      setLocalNotes(prevNotes => prevNotes.filter(note => !note.id.startsWith('temp-')));
    } finally {
      setIsLoading(false);
    }
  };

  // Handle note updates
  const handleNoteUpdate = (noteId: string, noteUpdates: Partial<Pick<Note, 'content' | 'title'>>) => {
    if (!noteId) return;
    
    // Don't send empty updates
    if (Object.keys(noteUpdates).length === 0) return;
    
    // Update local state immediately for instant feedback
    setLocalNotes(prevNotes => 
      prevNotes.map(note => 
        note.id === noteId ? { 
          ...note, 
          ...noteUpdates,
          updated_at: new Date().toISOString() // Update the local timestamp
        } : note
      )
    );
    
    // Debounce the database update
    debouncedUpdateNote(noteId, noteUpdates);
  };
  
  // Show delete confirmation dialog
  const confirmDelete = (note: Note) => {
    setNoteToDelete(note);
  };

  // Handle deleting a note
  const handleDeleteNote = async (id: string) => {
    if (!id) {
      console.error('No note ID provided for deletion');
      return;
    }
    
    console.log('=== STARTING DELETE ===');
    console.log('Note ID to delete:', id);
    console.log('Current activeNoteId:', activeNoteId);
    console.log('Current subscription:', subscription ? 'Active' : 'Inactive');
    
    setIsDeleting(true);
    
    // Store the current time for the fallback check
    const deleteStartTime = Date.now();
    let fallbackCheck: NodeJS.Timeout;
    
    // Function to send broadcast message
    const sendDeleteBroadcast = async () => {
      if (!subscription) {
        console.warn('No active subscription to send broadcast');
        return;
      }
      
      try {
        const response = await subscription.send({
          type: 'broadcast',
          event: 'note_deleted',
          payload: {
            noteId: id,
            roomId: roomId,
            deletedAt: new Date().toISOString()
          }
        });
        
        if (response === 'ok') {
          console.log('Delete broadcast sent successfully');
        } else {
          // The response is a string that could be 'error', 'timed out', or 'rate_limited'
          console.error('Broadcast failed with status:', response);
        }
      } catch (e) {
        console.error('Exception sending broadcast:', e);
      }
    };
    
    // Fallback function to verify the note was actually deleted
    const verifyNoteDeletion = async () => {
      try {
        console.log('Verifying note deletion for ID:', id);
        
        // If note is not in local state, consider it deleted
        const noteStillExistsLocally = localNotes.some(note => note.id === id);
        if (!noteStillExistsLocally) {
          console.log('Note not found in local state, assuming successful deletion');
          return;
        }
        
        // Verify with the server
        const { data, error } = await supabase
          .from('notes')
          .select('id')
          .eq('id', id)
          .maybeSingle();
          
        if (error) {
          if (error.code === 'PGRST116' || error.code === '406') {
            // Note not found (expected)
            console.log('Note deletion verified - note no longer exists');
          } else {
            console.warn('Error verifying note deletion:', error);
          }
        } else if (data) {
          // Note still exists, force refresh
          console.warn('Note still exists in database, forcing refresh...');
          await loadNotes();
        } else {
          // Note doesn't exist (expected)
          console.log('Note deletion verified - note no longer exists');
        }
      } catch (e) {
        console.error('Error in verifyNoteDeletion:', e);
        // Don't throw, just log the error
      }
    };
    
    // Set up the fallback check
    fallbackCheck = setTimeout(() => {
      console.log('Fallback: Verifying note deletion after delay...');
      verifyNoteDeletion();
    }, 2000); // Check after 2 seconds
    
    // Clean up the fallback check when done
    const cleanup = () => {
      if (fallbackCheck) clearTimeout(fallbackCheck);
    };
    
    // Optimistically remove the note from local state first
    console.log('Optimistically removing note from local state...');
    setLocalNotes(prevNotes => {
      const updatedNotes = prevNotes.filter(note => note.id !== id);
      console.log('Optimistic update - notes after removal:', updatedNotes.length);
      return updatedNotes;
    });
    
    // Clear active note if it's the one being deleted
    if (activeNoteId === id) {
      console.log('Clearing active note as it is being deleted');
      setActiveNoteId(null);
    }
    
    try {
      // Delete from the database
      console.log('Sending delete request to database...');
      const { data, error, status, statusText } = await supabase
        .from('notes')
        .delete()
        .eq('id', id)
        .select();  // Add select to get the deleted record

      console.log('Delete response:', { 
        status, 
        statusText, 
        error,
        data
      });
      
      if (error) {
        console.error('Database delete error:', error);
        throw error;
      }
      
      console.log('Delete successful, waiting for real-time event...');
      
      // Send broadcast to notify other clients
      await sendDeleteBroadcast();
      
      // Clean up the fallback check since the operation was successful
      cleanup();
      
      // Perform a quick verification after a short delay
      setTimeout(() => {
        verifyNoteDeletion();
      }, 500);
      
    } catch (error) {
      console.error('Error in handleDeleteNote:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete note';
      setError(errorMessage);
      
      // Re-fetch notes to restore the correct state
      console.log('Refetching notes to restore state...');
      try {
        await loadNotes();
      } finally {
        // Ensure we clean up the fallback check
        cleanup();
      }
    } finally {
      setIsDeleting(false);
      setNoteToDelete(null);
    }
  };

  // Cancel delete operation
  const cancelDelete = () => {
    setNoteToDelete(null);
  };

  const handleDeleteClick = (e: React.MouseEvent, note: Note) => {
    e.stopPropagation();
    confirmDelete(note);
  };

  const handleConfirmDelete = () => {
    if (noteToDelete) {
      handleDeleteNote(noteToDelete.id);
    }
  };

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center text-red-500">
        {error}
      </div>
    );
  }

  if (!roomId) {
    return (
      <div className="h-full flex items-center justify-center text-slate-500">
        No room selected. Please join a room to view notes.
      </div>
    );
  }

  return (
    <div className="h-full flex bg-slate-100 text-slate-800">
      {/* Sidebar */}
      <div className="w-1/4 min-w-[280px] max-w-[360px] bg-slate-50 border-r border-slate-200 flex flex-col">
        <div className="p-4 border-b border-slate-200">
          <Button
            onClick={addNewNote}
            className="w-full bg-slate-800 hover:bg-slate-700 text-slate-100 font-semibold mb-3"
            disabled={isLoading || isReadOnly}
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FilePlus className="w-4 h-4 mr-2" />
            )}
            New Note
          </Button>
          <div className="relative">
            <Input 
              type="text"
              placeholder="Search notes..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="pl-8 bg-white border-slate-300 focus:border-slate-500"
              disabled={isLoading}
            />
            <Search className="w-4 h-4 absolute left-2.5 top-1/2 transform -translate-y-1/2 text-slate-400" />
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto">
          {sortedNotes.length > 0 ? (
            sortedNotes.map(note => (
              <div
                key={note.id}
                onClick={() => setActiveNoteId(note.id)}
                className={`flex items-center justify-between p-3 m-2 rounded-lg cursor-pointer transition-colors 
                            ${activeNoteId === note.id 
                              ? 'bg-slate-200 border-slate-400 ring-1 ring-slate-400'
                              : 'hover:bg-slate-100'}`}
              >
                <div className="flex-1 overflow-hidden">
                  <h3 className={`font-medium truncate text-sm ${activeNoteId === note.id ? 'text-slate-800 font-semibold' : 'text-slate-700'}`}>{note.title || "Untitled Note"}</h3>
                  <p className="text-xs text-slate-500 truncate">{note.content?.substring(0,60) || "Empty note..."}</p>
                  <p className="text-xs text-slate-400 mt-1">{formatDate(note.updated_at || note.created_at)}</p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={(e) => handleDeleteClick(e, note)}
                  className="ml-2 text-slate-400 hover:text-red-500 hover:bg-red-100 rounded-full w-7 h-7 flex-shrink-0"
                  aria-label="Delete note"
                  disabled={isReadOnly}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))
          ) : (
            <div className="p-4 text-center text-slate-500">
              <StickyNote className="w-12 h-12 mx-auto mb-2 text-slate-400" />
              <p className="text-sm">No notes found.</p>
              {searchTerm && <p className="text-xs">Try a different search.</p>}
            </div>
          )}
        </div>
      </div>

      {/* Editor Pane */}
      <div className="flex-1 p-6 flex flex-col overflow-y-auto">
        {activeNote ? (
          <>
            <div className="flex items-center justify-between mb-4">
              <Input 
                ref={titleInputRef}
                value={activeNote.title}
                onChange={(e) => handleNoteUpdate(activeNote.id, { title: e.target.value })}
                placeholder="Note Title"
                className="text-2xl font-semibold border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none py-2 h-auto text-slate-800 placeholder:text-slate-400"
                disabled={isReadOnly}
              />
              <Button 
                variant="ghost" 
                size="icon"
                onClick={(e) => handleDeleteClick(e, activeNote)}
                className="text-slate-400 hover:text-red-500 hover:bg-red-100 rounded-full w-9 h-9"
                disabled={isReadOnly}
              >
                <Trash2 className="w-5 h-5" />
              </Button>
            </div>
            <Textarea 
              value={activeNote.content}
              onChange={(e) => handleNoteUpdate(activeNote.id, { content: e.target.value })}
              placeholder="Start writing your note here..."
              className="flex-1 w-full text-base p-2 rounded-md bg-white border border-slate-200 focus:border-slate-500 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none resize-none min-h-[300px] text-slate-700 placeholder:text-slate-400"
              disabled={isReadOnly}
            />
            <div className="mt-3 text-xs text-slate-500">
              <p className="text-xs text-slate-500">
                Last updated {formatDate(activeNote.updated_at || activeNote.created_at)}
              </p>
            </div>
          </>
        ) : (
          <div className="m-auto text-center text-slate-500">
            <FileText className="w-20 h-20 mx-auto mb-4 text-slate-400" />
            <h3 className="text-xl font-semibold mb-2">No Note Selected</h3>
            <p className="text-sm">Select a note from the sidebar or create a new one.</p>
          </div>
        )}
      </div>
      
      <DeleteConfirmation
        isOpen={!!noteToDelete}
        onClose={cancelDelete}
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
        title="Delete Note"
        description="Are you sure you want to delete this note? This action cannot be undone."
      />
    </div>
  );
};
